import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { PhotoItem } from '../../lib/photo-sources/types';

/**
 * Full-bleed single-photo view. This is where "cinematic" lives — as a detail mode rather than
 * the browse layout, because a one-photo-per-viewport scroll over hundreds of photos is a chore,
 * not a gallery.
 *
 * In-place overlay rather than a new tab (PLAN.md §7 D3): no token handoff, no re-fetching
 * blobs in a fresh JS context.
 */

interface CinematicViewProps {
  items: PhotoItem[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

function viewportRequest() {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  return { w: Math.round(window.innerWidth * dpr), h: Math.round(window.innerHeight * dpr) };
}

const CinematicView: React.FC<CinematicViewProps> = ({ items, index, onIndexChange, onClose }) => {
  const item = items[index];
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusTo = useRef<Element | null>(null);
  const reduceMotion = useReducedMotion();

  const go = useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next >= 0 && next < items.length) onIndexChange(next);
    },
    [index, items.length, onIndexChange]
  );

  // Resolve the current photo at viewport resolution, and warm the neighbours so arrowing
  // through doesn't stutter.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setUrl(null);

    const size = viewportRequest();
    item
      .resolve(size)
      .then((resolved) => {
        if (cancelled) return;
        setUrl(resolved);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    const neighbours = [items[index - 1], items[index + 1]].filter(Boolean) as PhotoItem[];
    for (const neighbour of neighbours) neighbour.resolve(size).catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [item, index, items]);

  // Keyboard, focus, and scroll lock.
  useEffect(() => {
    restoreFocusTo.current = document.activeElement;
    containerRef.current?.focus();

    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowRight') go(1);
      else if (event.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.documentElement.style.overflow = previousOverflow;
      if (restoreFocusTo.current instanceof HTMLElement) restoreFocusTo.current.focus();
    };
  }, [go, onClose]);

  const atStart = index === 0;
  const atEnd = index === items.length - 1;

  // Swipe. On a phone the arrow buttons are a fallback, not the primary way through.
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (event: React.TouchEvent) => {
    const touch = event.changedTouches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };
  const onTouchEnd = (event: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    // Ignore mostly-vertical drags so a swipe down to dismiss doesn't page sideways.
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
    go(dx < 0 ? 1 : -1);
  };

  return (
    <motion.div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${items.length}`}
      tabIndex={-1}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.25 }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="fixed inset-0 z-50 flex touch-pan-y items-center justify-center bg-black outline-none"
      style={{ height: '100dvh' }}
    >
      {/* Placeholder colour fills the frame until the full-resolution bytes land. */}
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{ backgroundColor: item.placeholder, opacity: url ? 0 : 0.5 }}
      />

      <AnimatePresence mode="wait">
        {url && (
          <motion.img
            key={item.id}
            src={url}
            alt=""
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-h-full max-w-full object-contain"
          />
        )}
      </AnimatePresence>

      {loading && !url && (
        <div className="relative text-xs text-white/50">Loading full resolution…</div>
      )}

      {/* Controls */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-3 top-[max(0.75rem,env(safe-area-inset-top))] rounded-full bg-black/50 p-3 text-white/80 backdrop-blur transition hover:bg-black/70 hover:text-white"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>

      {!atStart && (
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Previous photo"
          className="absolute left-3 rounded-full bg-black/40 p-2 text-white/70 backdrop-blur transition hover:bg-black/70 hover:text-white"
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </button>
      )}

      {!atEnd && (
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Next photo"
          className="absolute right-3 rounded-full bg-black/40 p-2 text-white/70 backdrop-blur transition hover:bg-black/70 hover:text-white"
        >
          <ChevronRightIcon className="h-6 w-6" />
        </button>
      )}

      <div className="absolute bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-[11px] text-white/60 backdrop-blur">
        {index + 1} / {items.length}
      </div>
    </motion.div>
  );
};

export default CinematicView;
